import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { cancelService } from '@/modules/Service/services'
import { Service, ServiceCancelProps } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { formatDateWithFormatter } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaCancelServiceModal } from './schema'
import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'

export interface ServiceCancelledModalProps extends ModalProps {
    service: Service
}

const CreateCreditModal = ({
    onClose,
    open,
    service,
}: ServiceCancelledModalProps) => {
    const { t } = useTranslation()
    const { refetch: callbackTicketWorkflow } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const { refetch: callbackService } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const formControl = useForm<ServiceCancelProps>({
        defaultValues: {
            service_stopped_date: service?.service_stopped_date || dayjs(),
            service_reason_cancel: '',
        },
        // @ts-ignore
        resolver: yupResolver(schemaCancelServiceModal),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: cancelService,
        },
        {
            successMessage: t('cancel-service-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
            onSuccess: () => {
                onClose()
                callbackTicketWorkflow()
                callbackService()
            },
        }
    )

    const handleCreate = (values: ServiceCancelProps) => {
        mutateAsync({
            serviceID: service?.service_id,
            data: {
                ...values,
                service_stopped_date: formatDateWithFormatter(
                    values?.service_stopped_date || '',
                    'YYYY-MM-DD'
                ),
            },
        })
    }

    return (
        <Dialog title={t('cancel-service')} onClose={onClose} open={open}>
            <Alert variant="warning" className="">
                {t('service-cancelled-notice')}
            </Alert>
            <GridHookForm
                className="mt-4"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('cancel-service'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'service_stopped_date',
                            type: 'datepicker',
                            label: t('service_stopped_date'),
                            placeholder: t('service_stopped_date'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'service_reason_cancel',
                            type: 'textarea',
                            label: t('reason-cancel-service'),
                            placeholder: t('reason-cancel-service'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateCreditModal
